import { useTranslation } from '@silospot/i18n/react';
import type { VisitSubScores } from '@silospot/shared';
import { toast } from '@silospot/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, ShieldBan, ShieldCheck } from 'lucide-react';
import { type ReactElement, type ReactNode, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Badge,
  Btn,
  Card,
  CardHead,
  PageHeader,
  Verdict,
  VerdictScale,
} from '../../components/cld';
import { BlocklistModal } from '../../components/cld/blocklist-modal';
import { Flag } from '../../components/location';
import { api, serverError } from '../../lib/api';
import { normalizeTrustedIp } from '../../lib/whitelist';

/** ms → "Xs" / "Xm Ys" (— when unknown). */
function fmtDur(ms: number): string {
  if (ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
}

function Row({ label, children }: { label: ReactNode; children: ReactNode }): ReactElement {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[#f1f3f6] py-2 text-[13px] last:border-0">
      <span className="text-text-2">{label}</span>
      <span className="text-right font-medium text-text">{children}</span>
    </div>
  );
}

function ScoreBar({ label, value }: { label: string; value: number }): ReactElement {
  const color =
    value >= 70 ? 'var(--fraud-fg)' : value >= 40 ? 'var(--suspect-fg)' : 'var(--clean-fg)';
  return (
    <div>
      <div className="mb-1 flex justify-between text-[12.5px]">
        <span className="text-text-2">{label}</span>
        <span className="font-mono" style={{ color }}>
          {value}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-neutral-bg">
        <div className="h-full rounded-full" style={{ width: `${value}%`, background: color }} />
      </div>
    </div>
  );
}

export function VisitorPage(): ReactElement {
  const { t } = useTranslation('pages');
  const { id = '', visitId = '' } = useParams();
  const qc = useQueryClient();
  const [bl, setBl] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['cld-visitor', id, visitId],
    queryFn: () => api.getVisitDetail(id, visitId),
    enabled: !!id && !!visitId,
  });

  // Mark this visitor's IP as a trusted real client (whitelist). The whitelist is
  // sovereign: a real_client verdict permanently exempts the IP (or its IPv6 /64)
  // from every block/exclusion recommendation.
  const trust = useMutation({
    mutationFn: () => {
      const ip = data ? normalizeTrustedIp(data.ip) : null;
      if (!ip) throw new Error(t('visitor.trustInvalid'));
      return api.postVerdict(id, { ip, verdict: 'real_client' });
    },
    onSuccess: () => {
      toast.success(t('visitor.trusted'));
      void qc.invalidateQueries({ queryKey: ['cld-visitor', id, visitId] });
    },
    onError: (e) => toast.error(serverError(e) ?? t('visitor.trustInvalid')),
  });
  // Revoke trust immediately (history preserved). Idempotent: no-op if not trusted.
  const untrust = useMutation({
    mutationFn: () => {
      const ip = data ? normalizeTrustedIp(data.ip) : null;
      if (!ip) throw new Error(t('visitor.trustInvalid'));
      return api.revokeTrust(id, ip);
    },
    onSuccess: (r) => {
      toast.success(r.revoked > 0 ? t('visitor.untrusted') : t('visitor.untrustedNone'));
      void qc.invalidateQueries({ queryKey: ['cld-visitor', id, visitId] });
    },
    onError: (e) => toast.error(serverError(e) ?? t('visitor.trustInvalid')),
  });

  if (isLoading) {
    return (
      <div>
        <PageHeader title={t('visitor.title')} />
        <Card>
          <div className="h-64 animate-pulse rounded-lg bg-neutral-bg" />
        </Card>
      </div>
    );
  }
  if (!data) {
    return (
      <div>
        <PageHeader title={t('visitor.title')} />
        <Card>
          <p className="py-10 text-center text-[13px] text-text-3">{t('visitor.notFound')}</p>
        </Card>
      </div>
    );
  }

  const o = data.origin;
  const r = data.recidivism;
  const subs: { key: keyof VisitSubScores; label: string }[] = [
    { key: 'behavior', label: t('visitor.behavior') },
    { key: 'network', label: t('visitor.network') },
    { key: 'fingerprint', label: t('signal.fingerprint') },
    { key: 'security', label: t('visitor.verdict') },
    { key: 'proxyPattern', label: t('signal.proxy') },
  ];
  const autoTone =
    o.automation === 'automated' ? 'fraud' : o.automation === 'real' ? 'clean' : 'neutral';

  return (
    <div>
      <PageHeader
        title={t('visitor.title')}
        sub={
          <button
            type="button"
            title={t('ip.clickToCopy')}
            onClick={() => {
              void navigator.clipboard.writeText(data.ip);
              toast.success(t('ip.copied'));
            }}
            className="inline-flex items-center gap-1.5 font-mono text-[14px] font-semibold text-text hover:text-primary"
          >
            {data.ip || '—'}
            <Copy className="h-3.5 w-3.5 text-text-3" />
          </button>
        }
        actions={
          <>
            <Verdict score={data.fraudScore} confirmed={data.isBot} />
            <Btn
              icon={ShieldCheck}
              disabled={trust.isPending || !data.ip}
              onClick={() => trust.mutate()}
            >
              {t('visitor.markRealClient')}
            </Btn>
            <Btn disabled={untrust.isPending || !data.ip} onClick={() => untrust.mutate()}>
              {t('visitor.revokeTrust')}
            </Btn>
            <Btn icon={ShieldBan} onClick={() => setBl(true)}>
              {t('blocklist.challenge')} / {t('blocklist.block')}
            </Btn>
          </>
        }
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="lg:col-span-2">
          <CardHead title={t('visitor.score')} />
          <div className="max-w-xl">
            <VerdictScale score={data.fraudScore} />
          </div>
          {data.clientReasons.length > 0 ? (
            <div className="mt-4">
              <div className="mb-2 text-[12.5px] font-semibold text-text-2">
                {t('visitor.reasons')}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.clientReasons.map((reason) => (
                  <Badge key={reason} tone="suspect">
                    {reason}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}
        </Card>

        <Card>
          <CardHead title={t('visitor.identity')} />
          <Row label={t('geo.country')}>
            <Flag country={o.country} /> {o.country || '—'}
          </Row>
          <Row label={t('geo.city')}>{o.city || '—'}</Row>
          <Row label={t('audience.browsers')}>{o.browser || '—'}</Row>
          <Row label={t('audience.os')}>{o.os || '—'}</Row>
          <Row label={t('profile.language')}>{o.language || '—'}</Row>
          <Row label="Fuseau">{o.timezone || '—'}</Row>
          <Row label={t('visitor.browser')}>
            <Badge tone={autoTone}>{o.automation}</Badge>
          </Row>
        </Card>

        <Card>
          <CardHead title={t('visitor.network')} />
          <Row label={t('visitor.asn')}>{o.asn ? `AS${o.asn}` : '—'}</Row>
          <Row label={t('visitor.operator')}>{o.asnName || '—'}</Row>
          <Row label={t('fraud.colThreatType')}>
            <Badge tone={o.proxyProbable ? 'info' : 'neutral'}>
              {o.proxyProbable ? t('signal.proxy') : o.connType}
            </Badge>
          </Row>
          {/* Recurrence over the rolling window — labelled explicitly (these were three
              ambiguous rows before: "Pays (IP)"/"ASN"/"Navigateur" all showing counts). */}
          <Row label={t('visitor.recIp')}>{r.ip}×</Row>
          <Row label={t('visitor.recAsn')}>{r.asn}×</Row>
          <Row label={t('visitor.recFp')}>
            {r.fingerprint}× · {r.fingerprintIps} IP
          </Row>
        </Card>

        <Card>
          <CardHead title={t('visitor.behavior')} />
          <div className="flex flex-col gap-3">
            {subs.map((s) => (
              <ScoreBar key={s.key} label={s.label} value={data.subScores[s.key]} />
            ))}
          </div>
        </Card>

        <Card>
          <CardHead title={t('visitor.pages')} />
          {data.journey.length === 0 ? (
            <p className="py-4 text-center text-[13px] text-text-3">—</p>
          ) : (
            <ol className="flex flex-col gap-1.5">
              {data.journey.map((step, i) => (
                <li key={`${step.label}-${i}`} className="flex items-center gap-2 text-[12.5px]">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-accent-bg font-mono text-[10px] text-primary">
                    {i + 1}
                  </span>
                  <span className="flex-1 truncate font-mono text-text-2">{step.label}</span>
                  <span className="text-text-3" title={t('visitor.timeOnPage')}>
                    {step.dwellMs >= 0 ? fmtDur(step.dwellMs) : '—'}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      {/* Internal technical id, demoted (the IP is the headline identity above). */}
      <div className="mt-3 font-mono text-[10.5px] text-text-3">
        {t('visitor.internalId')}: {visitId}
      </div>

      {bl ? (
        <BlocklistModal
          projectId={id}
          prefill={{
            ip: data.ip,
            connType: data.origin.connType,
            proxyProbable: data.origin.proxyProbable,
          }}
          onClose={() => setBl(false)}
        />
      ) : null}
    </div>
  );
}
